// src/components/portal/InventoryView.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  getDocs,
  setDoc,
  updateDoc,
  writeBatch,
  increment,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useBodyScrollLock } from '../../utilities/useBodyScrollLock';

// ─── Constants ────────────────────────────────────────────────────────────────

const MOVEMENT_TYPES = [
  { value: 'received',   label: 'Received',   sign: +1 },
  { value: 'waste',      label: 'Waste',      sign: -1 },
  { value: 'adjustment', label: 'Adjustment', sign: +1 },
  { value: 'transfer',   label: 'Transfer',   sign: -1 },
  { value: 'depletion',  label: 'Depletion',  sign: -1 },
];

const WASTE_REASONS = [
  { value: 'spoilage',   label: 'Spoilage / Went Bad'  },
  { value: 'over_prep',  label: 'Over-prep'            },
  { value: 'dropped',    label: 'Dropped / Spilled'    },
  { value: 'sent_back',  label: 'Sent Back by Guest'   },
  { value: 'expired',    label: 'Expired'              },
  { value: 'theft',      label: 'Theft / Shrinkage'    },
  { value: 'other',      label: 'Other'                },
];

const UNITS = [
  'each','oz','lbs','g','kg','fl oz','cups','pints','quarts',
  'gallons','liters','ml','tsp','tbsp','slices','portions',
  'bunches','cloves','leaves',
];

const INGREDIENT_CATEGORIES = [
  'Proteins','Produce','Dairy','Dry Goods','Beverages',
  'Oils & Fats','Sauces & Condiments','Spices & Herbs','Other',
];

const STATUS_CONFIG = {
  out:         { label: 'Out',         bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500'    },
  critical:    { label: 'Critical',    bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-brand-blue' },
  low:         { label: 'Low',         bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  ok:          { label: 'OK',          bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500'  },
  overstocked: { label: 'Overstocked', bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-400'   },
  unknown:     { label: 'Unknown',     bg: 'bg-neutral-100',text: 'text-neutral-500',dot: 'bg-neutral-400' },
};

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function stockStatus({ currentQuantity: qty, parLevel, minimumQuantity, maximumQuantity }) {
  if (qty == null || !parLevel) return 'unknown';
  if (qty <= 0) return 'out';
  if (qty <= (minimumQuantity || 0)) return 'critical';
  if (qty < parLevel * 0.5) return 'low';
  if (maximumQuantity && qty > maximumQuantity) return 'overstocked';
  return 'ok';
}

function fmtQty(n) {
  if (n == null) return '—';
  const num = Number(n);
  if (isNaN(num)) return '—';
  return num % 1 === 0 ? num.toString() : num.toFixed(2);
}

function fmtCurrency(n) {
  if (n == null) return '—';
  const num = Number(n);
  if (isNaN(num)) return '—';
  return '$' + num.toFixed(2);
}

function fmtDate(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ─── Firestore write helpers ──────────────────────────────────────────────────

async function writeMovement(restaurantId, ing, type, delta, user, notes = '', wasteReason = '', lotDate = null) {
  const prev = Number(ing.currentQuantity) || 0;
  const next = prev + delta;
  const cost = Number(ing.currentUnitCost) || 0;

  const movRef = doc(collection(db, 'restaurants_data', restaurantId, 'inventoryMovements'));
  const ingRef = doc(db, 'restaurants_data', restaurantId, 'ingredients', ing.id);

  const ingUpdate = {
    currentQuantity: next,
    inventoryValue: next * cost,
    updatedAt: serverTimestamp(),
  };

  if (type === 'count') {
    ingUpdate.lastCountedAt    = serverTimestamp();
    ingUpdate.lastCountedBy    = user.accountId;
    ingUpdate.actualRemaining  = next;
    ingUpdate.variance         = next - (Number(ing.theoreticalRemaining) || 0);
  }
  if (type === 'received') {
    ingUpdate.lastReceivedDate = serverTimestamp();
  }

  const movDoc = {
    ingredientId:   ing.id,
    ingredientName: ing.name,
    ingredientUnit: ing.baseUnit,
    type,
    quantity:         delta,
    previousQuantity: prev,
    newQuantity:      next,
    timestamp:        serverTimestamp(),
    employeeId:       user.accountId,
    employeeName:     `${user.firstName} ${user.lastName}`,
    source:           'manual',
    notes,
  };
  if (type === 'waste' && wasteReason) movDoc.wasteReason = wasteReason;

  const writes = [setDoc(movRef, movDoc), updateDoc(ingRef, ingUpdate)];

  // Create lot record if this is a receive and the ingredient has a shelf life
  if (type === 'received' && ing.shelfLifeDays && delta > 0) {
    const receivedDate = lotDate ? new Date(lotDate) : new Date();
    const expiresAt    = new Date(receivedDate);
    expiresAt.setDate(expiresAt.getDate() + Number(ing.shelfLifeDays));
    const lotRef = doc(collection(db, 'restaurants_data', restaurantId, 'inventoryLots'));
    writes.push(setDoc(lotRef, {
      ingredientId:   ing.id,
      ingredientName: ing.name,
      quantity:       delta,
      unit:           ing.baseUnit,
      receivedDate:   receivedDate.toISOString().split('T')[0],
      expiresAt:      expiresAt.toISOString().split('T')[0],
      shelfLifeDays:  Number(ing.shelfLifeDays),
      status:         'active',
      createdAt:      serverTimestamp(),
      employeeId:     user.accountId,
      employeeName:   `${user.firstName} ${user.lastName}`,
      notes,
    }));
  }

  await Promise.all(writes);
}

// ─── Suggest-86 helper ────────────────────────────────────────────────────────
// After a movement drops an ingredient to/below its minimum, fetch all menu
// items that list this ingredient and return the ones not already 86'd.

async function findAffectedItems(restaurantId, ingredientId) {
  const snap = await getDocs(collection(db, 'restaurants_data', restaurantId, 'items'));
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(item =>
      !item.outOfStock &&
      Array.isArray(item.ingredients) &&
      item.ingredients.some(i => i.ingredientId === ingredientId)
    );
}

// ─── Suggest-86 Modal ─────────────────────────────────────────────────────────

function Suggest86Modal({ ingredientName, affectedItems, restaurantId, onDone }) {
  const [selected,  setSelected]  = useState(() => new Set(affectedItems.map(i => i.id)));
  const [saving,    setSaving]    = useState(false);
  const [error,     setError]     = useState('');

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleConfirm = async () => {
    if (selected.size === 0) { onDone(); return; }
    setSaving(true);
    setError('');
    try {
      const batch = writeBatch(db);
      selected.forEach(itemId => {
        batch.update(doc(db, 'restaurants_data', restaurantId, 'items', itemId), { outOfStock: true });
      });
      await batch.commit();
      onDone();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  return (
    <ModalOverlay onClose={onDone}>
      <div className="w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[85dvh]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {/* Header */}
        <div className="shrink-0 px-4 pt-4 pb-3 border-b border-neutral-100">
          <h2 className="text-base font-semibold text-neutral-800">Mark Items as 86'd?</h2>
          <p className="text-xs text-neutral-500 mt-1">
            <span className="font-medium text-orange-600">{ingredientName}</span> is now at or below its minimum.
            The following items use it — deselect any you don't want to 86.
          </p>
        </div>

        {/* Item list */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
          {affectedItems.map(item => (
            <label
              key={item.id}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${
                selected.has(item.id)
                  ? 'border-orange-300 bg-orange-50'
                  : 'border-neutral-200 bg-white'
              }`}
            >
              <input
                type="checkbox"
                checked={selected.has(item.id)}
                onChange={() => toggle(item.id)}
                className="accent-orange-500 w-4 h-4 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-800 truncate">{item.name}</p>
                {item.menu && <p className="text-xs text-neutral-400 truncate">{item.menu}</p>}
              </div>
            </label>
          ))}
        </div>

        {/* Footer */}
        <div className="shrink-0 flex gap-2 px-4 py-3 border-t border-neutral-100">
          <button
            onClick={onDone}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl border border-neutral-200 text-sm font-medium text-neutral-600 hover:bg-neutral-50"
          >
            Skip
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving || selected.size === 0}
            className="flex-1 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-50"
          >
            {saving ? 'Saving…' : `86 ${selected.size} Item${selected.size !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ─── Shared modal wrapper ─────────────────────────────────────────────────────

function ModalOverlay({ onClose, children }) {
  useBodyScrollLock(true);
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 px-0 sm:px-4 sm:py-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {children}
    </div>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ─── Ingredient Modal (Add / Edit) — 4 tabs ───────────────────────────────────

const EMPTY_ING = {
  name: '', sku: '', category: '', description: '', active: true,
  baseUnit: 'each', purchaseUnit: 'each', recipeUnit: 'each',
  baseToRecipeConversion: 1, purchaseToBaseConversion: 1,
  currentQuantity: '', parLevel: '', minimumQuantity: '', maximumQuantity: '',
  currentUnitCost: '', averageCost: '', lastPurchasePrice: '',
  supplierName: '', supplierContact: '', supplierLeadTimeDays: '',
  defaultSupplierId: '', supplierSku: '',
  shelfLifeDays: '',
  theoreticalRemaining: '', yieldPercentage: 100,
  trackDepletion: false, depletionPerCover: '', depletionUnit: '',
  wasteTracking: false, allowedWastePercent: '',
  countingEnabled: true, countingFrequency: 'daily', countingOrder: '',
  forecastingEnabled: false,
  lastCountedAt: null, lastCountedBy: '', lastReceivedDate: null,
  inventoryValue: 0, variance: 0, actualRemaining: '', notes: '',
};

function IngredientModal({ ingredient, restaurantId, onClose, onSaved }) {
  const [tab, setTab]         = useState('info');
  const [form, setForm]       = useState(ingredient ? { ...EMPTY_ING, ...ingredient } : { ...EMPTY_ING });
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    getDocs(collection(db, 'restaurants_data', restaurantId, 'suppliers'))
      .then(snap => setSuppliers(snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(s => s.isActive !== false)))
      .catch(() => {});
  }, [restaurantId]);

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required.'); setTab('info'); return; }
    if (!form.baseUnit)    { setError('Base unit is required.'); setTab('stock'); return; }
    setError('');
    setSaving(true);
    try {
      const isNew = !ingredient;
      const ref   = isNew
        ? doc(collection(db, 'restaurants_data', restaurantId, 'ingredients'))
        : doc(db, 'restaurants_data', restaurantId, 'ingredients', ingredient.id);

      const data = {
        ...form,
        id:          ref.id,
        currentQuantity:    Number(form.currentQuantity)    || 0,
        parLevel:           Number(form.parLevel)           || 0,
        minimumQuantity:    Number(form.minimumQuantity)    || 0,
        maximumQuantity:    form.maximumQuantity !== '' ? Number(form.maximumQuantity) : null,
        currentUnitCost:    Number(form.currentUnitCost)    || 0,
        averageCost:        Number(form.averageCost)        || 0,
        lastPurchasePrice:  Number(form.lastPurchasePrice)  || 0,
        yieldPercentage:    Number(form.yieldPercentage)    || 100,
        depletionPerCover:  form.depletionPerCover !== '' ? Number(form.depletionPerCover) : null,
        allowedWastePercent: form.allowedWastePercent !== '' ? Number(form.allowedWastePercent) : null,
        supplierLeadTimeDays: form.supplierLeadTimeDays !== '' ? Number(form.supplierLeadTimeDays) : null,
        baseToRecipeConversion:   Number(form.baseToRecipeConversion)   || 1,
        purchaseToBaseConversion: Number(form.purchaseToBaseConversion) || 1,
        countingOrder:    Number(form.countingOrder) || 0,
        updatedAt: serverTimestamp(),
        ...(isNew ? { createdAt: serverTimestamp() } : {}),
      };
      data.inventoryValue = (data.currentQuantity * data.currentUnitCost);

      await setDoc(ref, data, { merge: true });
      onSaved && onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const TABS = ['info', 'stock', 'cost', 'settings'];
  const TAB_LABELS = { info: 'Info', stock: 'Stock & Units', cost: 'Cost & Vendor', settings: 'Settings' };

  return (
    <ModalOverlay onClose={onClose}>
      <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[92dvh]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-4 pt-4 pb-3 border-b border-neutral-100">
          <h2 className="text-base font-semibold text-neutral-800">
            {ingredient ? 'Edit Ingredient' : 'Add Ingredient'}
          </h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">&times;</button>
        </div>

        {/* Tab bar */}
        <div className="shrink-0 flex border-b border-neutral-100 px-4">
          {TABS.map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`py-2 px-3 text-xs font-medium border-b-2 transition-colors ${
                tab === t
                  ? 'border-brand-blue text-brand-blue'
                  : 'border-transparent text-neutral-400 hover:text-neutral-600'
              }`}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
          )}

          {/* ── Info tab ── */}
          {tab === 'info' && (
            <>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Name *</label>
                <input
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="e.g. Roma Tomatoes"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">SKU / Item Code</label>
                <input
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                  value={form.sku}
                  onChange={e => set('sku', e.target.value)}
                  placeholder="e.g. TOM-001"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Category</label>
                <select
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                  value={form.category}
                  onChange={e => set('category', e.target.value)}
                >
                  <option value="">Select category…</option>
                  {INGREDIENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Description / Notes</label>
                <textarea
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm resize-none"
                  rows={3}
                  value={form.description}
                  onChange={e => set('description', e.target.value)}
                  placeholder="Optional notes about this ingredient"
                />
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-sm text-neutral-700">Active</span>
                <button
                  type="button"
                  onClick={() => set('active', !form.active)}
                  className={`w-10 h-6 rounded-full transition-colors ${form.active ? 'bg-brand-blue' : 'bg-neutral-200'}`}
                >
                  <span className={`block w-4 h-4 rounded-full bg-white shadow mx-1 transition-transform ${form.active ? 'translate-x-4' : ''}`} />
                </button>
              </div>
            </>
          )}

          {/* ── Stock & Units tab ── */}
          {tab === 'stock' && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Base Unit *</label>
                  <select
                    className="w-full border border-neutral-200 rounded-lg px-2 py-2 text-sm"
                    value={form.baseUnit}
                    onChange={e => set('baseUnit', e.target.value)}
                  >
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Recipe Unit</label>
                  <select
                    className="w-full border border-neutral-200 rounded-lg px-2 py-2 text-sm"
                    value={form.recipeUnit}
                    onChange={e => set('recipeUnit', e.target.value)}
                  >
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Purchase Unit</label>
                  <select
                    className="w-full border border-neutral-200 rounded-lg px-2 py-2 text-sm"
                    value={form.purchaseUnit}
                    onChange={e => set('purchaseUnit', e.target.value)}
                  >
                    {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Base→Recipe Conv.</label>
                  <input type="number" min="0" step="any"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.baseToRecipeConversion}
                    onChange={e => set('baseToRecipeConversion', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Purchase→Base Conv.</label>
                  <input type="number" min="0" step="any"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.purchaseToBaseConversion}
                    onChange={e => set('purchaseToBaseConversion', e.target.value)}
                  />
                </div>
              </div>
              <hr className="border-neutral-100" />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Current Qty</label>
                  <input type="number" min="0" step="any"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.currentQuantity}
                    onChange={e => set('currentQuantity', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Par Level</label>
                  <input type="number" min="0" step="any"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.parLevel}
                    onChange={e => set('parLevel', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Minimum Qty</label>
                  <input type="number" min="0" step="any"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.minimumQuantity}
                    onChange={e => set('minimumQuantity', e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Maximum Qty</label>
                  <input type="number" min="0" step="any"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.maximumQuantity}
                    onChange={e => set('maximumQuantity', e.target.value)}
                    placeholder="No limit"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Yield %</label>
                <input type="number" min="0" max="100" step="0.1"
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                  value={form.yieldPercentage}
                  onChange={e => set('yieldPercentage', e.target.value)}
                />
              </div>
            </>
          )}

          {/* ── Cost & Vendor tab ── */}
          {tab === 'cost' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Current Unit Cost</label>
                  <input type="number" min="0" step="0.01"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.currentUnitCost}
                    onChange={e => set('currentUnitCost', e.target.value)}
                    placeholder="$0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Average Cost</label>
                  <input type="number" min="0" step="0.01"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.averageCost}
                    onChange={e => set('averageCost', e.target.value)}
                    placeholder="$0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Last Purchase Price</label>
                  <input type="number" min="0" step="0.01"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.lastPurchasePrice}
                    onChange={e => set('lastPurchasePrice', e.target.value)}
                    placeholder="$0.00"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Supplier Lead Time (days)</label>
                  <input type="number" min="0" step="1"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.supplierLeadTimeDays}
                    onChange={e => set('supplierLeadTimeDays', e.target.value)}
                    placeholder="—"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Shelf Life (days)</label>
                  <input type="number" min="0" step="1"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.shelfLifeDays}
                    onChange={e => set('shelfLifeDays', e.target.value)}
                    placeholder="e.g. 7"
                  />
                </div>
              </div>
              <hr className="border-neutral-100" />
              <div>
                <label className="block text-xs font-medium text-neutral-500 mb-1">Default Supplier</label>
                {suppliers.length > 0 ? (
                  <select
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.defaultSupplierId}
                    onChange={e => {
                      const sup = suppliers.find(s => s.id === e.target.value);
                      set('defaultSupplierId', e.target.value);
                      if (sup) set('supplierName', sup.name);
                    }}
                  >
                    <option value="">None / manual</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                ) : (
                  <input
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.supplierName}
                    onChange={e => set('supplierName', e.target.value)}
                    placeholder="e.g. US Foods (add suppliers in Suppliers tab)"
                  />
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Supplier SKU</label>
                  <input
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.supplierSku}
                    onChange={e => set('supplierSku', e.target.value)}
                    placeholder="Vendor item #"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Supplier Contact</label>
                  <input
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.supplierContact}
                    onChange={e => set('supplierContact', e.target.value)}
                    placeholder="Phone or email"
                  />
                </div>
              </div>
            </>
          )}

          {/* ── Settings tab ── */}
          {tab === 'settings' && (
            <>
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Depletion</p>
              <div className="flex items-center justify-between py-1">
                <span className="text-sm text-neutral-700">Track Depletion</span>
                <button
                  type="button"
                  onClick={() => set('trackDepletion', !form.trackDepletion)}
                  className={`w-10 h-6 rounded-full transition-colors ${form.trackDepletion ? 'bg-brand-blue' : 'bg-neutral-200'}`}
                >
                  <span className={`block w-4 h-4 rounded-full bg-white shadow mx-1 transition-transform ${form.trackDepletion ? 'translate-x-4' : ''}`} />
                </button>
              </div>
              {form.trackDepletion && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1">Depletion Per Cover</label>
                    <input type="number" min="0" step="any"
                      className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                      value={form.depletionPerCover}
                      onChange={e => set('depletionPerCover', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1">Depletion Unit</label>
                    <select
                      className="w-full border border-neutral-200 rounded-lg px-2 py-2 text-sm"
                      value={form.depletionUnit}
                      onChange={e => set('depletionUnit', e.target.value)}
                    >
                      <option value="">—</option>
                      {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </div>
                </div>
              )}

              <hr className="border-neutral-100" />
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Waste</p>
              <div className="flex items-center justify-between py-1">
                <span className="text-sm text-neutral-700">Track Waste</span>
                <button
                  type="button"
                  onClick={() => set('wasteTracking', !form.wasteTracking)}
                  className={`w-10 h-6 rounded-full transition-colors ${form.wasteTracking ? 'bg-brand-blue' : 'bg-neutral-200'}`}
                >
                  <span className={`block w-4 h-4 rounded-full bg-white shadow mx-1 transition-transform ${form.wasteTracking ? 'translate-x-4' : ''}`} />
                </button>
              </div>
              {form.wasteTracking && (
                <div>
                  <label className="block text-xs font-medium text-neutral-500 mb-1">Allowed Waste %</label>
                  <input type="number" min="0" max="100" step="0.1"
                    className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                    value={form.allowedWastePercent}
                    onChange={e => set('allowedWastePercent', e.target.value)}
                  />
                </div>
              )}

              <hr className="border-neutral-100" />
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">Counting</p>
              <div className="flex items-center justify-between py-1">
                <span className="text-sm text-neutral-700">Enable Counting</span>
                <button
                  type="button"
                  onClick={() => set('countingEnabled', !form.countingEnabled)}
                  className={`w-10 h-6 rounded-full transition-colors ${form.countingEnabled ? 'bg-brand-blue' : 'bg-neutral-200'}`}
                >
                  <span className={`block w-4 h-4 rounded-full bg-white shadow mx-1 transition-transform ${form.countingEnabled ? 'translate-x-4' : ''}`} />
                </button>
              </div>
              {form.countingEnabled && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1">Frequency</label>
                    <select
                      className="w-full border border-neutral-200 rounded-lg px-2 py-2 text-sm"
                      value={form.countingFrequency}
                      onChange={e => set('countingFrequency', e.target.value)}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Bi-Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-neutral-500 mb-1">Count Order</label>
                    <input type="number" min="0" step="1"
                      className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                      value={form.countingOrder}
                      onChange={e => set('countingOrder', e.target.value)}
                      placeholder="0"
                    />
                  </div>
                </div>
              )}
              {form.lastCountedAt && (
                <p className="text-xs text-neutral-400">Last counted: {fmtDate(form.lastCountedAt)}</p>
              )}

              <hr className="border-neutral-100" />
              <div className="flex items-center justify-between py-1">
                <span className="text-sm text-neutral-700">Enable Forecasting</span>
                <button
                  type="button"
                  onClick={() => set('forecastingEnabled', !form.forecastingEnabled)}
                  className={`w-10 h-6 rounded-full transition-colors ${form.forecastingEnabled ? 'bg-brand-blue' : 'bg-neutral-200'}`}
                >
                  <span className={`block w-4 h-4 rounded-full bg-white shadow mx-1 transition-transform ${form.forecastingEnabled ? 'translate-x-4' : ''}`} />
                </button>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 flex gap-2 px-4 py-3 border-t border-neutral-100">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-neutral-200 text-sm font-medium text-neutral-600 hover:bg-neutral-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-hover disabled:opacity-50"
          >
            {saving ? 'Saving…' : ingredient ? 'Save Changes' : 'Add Ingredient'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ─── Adjust Stock Modal ───────────────────────────────────────────────────────

function AdjustStockModal({ ingredient, restaurantId, user, onClose }) {
  const [type,           setType]           = useState('received');
  const [qty,            setQty]            = useState('');
  const [notes,          setNotes]          = useState('');
  const [wasteReason,    setWasteReason]    = useState('');
  const [lotDate,        setLotDate]        = useState('');
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState('');
  const [suggest86Items, setSuggest86Items] = useState(null); // null = not shown

  const movType = MOVEMENT_TYPES.find(m => m.value === type);

  const handleSave = async () => {
    const n = parseFloat(qty);
    if (!qty || isNaN(n) || n === 0) { setError('Enter a valid quantity.'); return; }
    if (type === 'waste' && !wasteReason) { setError('Select a waste reason.'); return; }
    setSaving(true);
    setError('');
    try {
      const delta = n * movType.sign;
      await writeMovement(restaurantId, ingredient, type, delta, user, notes.trim(), wasteReason, lotDate || null);
      const newQty = (Number(ingredient.currentQuantity) || 0) + delta;
      const min    = Number(ingredient.minimumQuantity) || 0;
      if (newQty <= min) {
        const affected = await findAffectedItems(restaurantId, ingredient.id);
        if (affected.length > 0) {
          setSuggest86Items(affected);
          return; // stay open — Suggest86Modal takes over
        }
      }
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (suggest86Items) {
    return (
      <Suggest86Modal
        ingredientName={ingredient.name}
        affectedItems={suggest86Items}
        restaurantId={restaurantId}
        onDone={onClose}
      />
    );
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[80dvh]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="shrink-0 flex items-center justify-between px-4 pt-4 pb-3 border-b border-neutral-100">
          <div>
            <h2 className="text-base font-semibold text-neutral-800">Adjust Stock</h2>
            <p className="text-xs text-neutral-400 mt-0.5">{ingredient.name}</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">&times;</button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}

          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Movement Type</label>
            <div className="grid grid-cols-2 gap-2">
              {MOVEMENT_TYPES.map(m => (
                <button
                  key={m.value}
                  onClick={() => setType(m.value)}
                  className={`py-2 rounded-lg text-sm font-medium border transition-colors ${
                    type === m.value
                      ? 'bg-brand-blue-light border-brand-blue text-brand-blue'
                      : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">
              Quantity ({ingredient.baseUnit || 'units'})
            </label>
            <input
              type="number"
              min="0"
              step="any"
              autoFocus
              className="w-full border border-neutral-200 rounded-lg px-3 py-2.5 text-sm"
              value={qty}
              onChange={e => setQty(e.target.value)}
              placeholder="0"
            />
            {qty && !isNaN(parseFloat(qty)) && (
              <p className="text-xs text-neutral-400 mt-1">
                Current: {fmtQty(ingredient.currentQuantity)} → New: {fmtQty((Number(ingredient.currentQuantity) || 0) + parseFloat(qty) * movType.sign)}
              </p>
            )}
          </div>

          {type === 'waste' && (
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Waste Reason <span className="text-red-400">*</span></label>
              <div className="grid grid-cols-2 gap-1.5">
                {WASTE_REASONS.map(r => (
                  <button
                    key={r.value}
                    onClick={() => setWasteReason(r.value)}
                    className={`py-2 px-2 rounded-lg text-xs font-medium border text-left transition-colors ${
                      wasteReason === r.value
                        ? 'bg-red-50 border-red-300 text-red-700'
                        : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {type === 'received' && ingredient.shelfLifeDays && (
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">
                Received Date <span className="text-neutral-300">(for expiry tracking)</span>
              </label>
              <input
                type="date"
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                value={lotDate}
                onChange={e => setLotDate(e.target.value)}
              />
              {lotDate && (
                <p className="text-xs text-neutral-400 mt-1">
                  Expires: {(() => {
                    const d = new Date(lotDate);
                    d.setDate(d.getDate() + Number(ingredient.shelfLifeDays));
                    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  })()}
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Notes (optional)</label>
            <input
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Reason, invoice #, etc."
            />
          </div>
        </div>

        <div className="shrink-0 flex gap-2 px-4 py-3 border-t border-neutral-100">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-neutral-200 text-sm font-medium text-neutral-600 hover:bg-neutral-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-hover disabled:opacity-50">
            {saving ? 'Saving…' : 'Record'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ─── Ingredient Card ──────────────────────────────────────────────────────────

function IngredientCard({ ing, onEdit, onAdjust, expiryStatus }) {
  const status = stockStatus(ing);
  const cfg    = STATUS_CONFIG[status];

  return (
    <div className={`bg-white border rounded-xl p-3 flex flex-col gap-2 shadow-sm ${expiryStatus === 'expired' ? 'border-red-300' : expiryStatus === 'expiring' ? 'border-orange-300' : 'border-neutral-100'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-neutral-800 truncate">{ing.name}</p>
          {ing.category && <p className="text-xs text-neutral-400">{ing.category}</p>}
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          <StatusBadge status={status} />
          {expiryStatus === 'expired' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" />Expired
            </span>
          )}
          {expiryStatus === 'expiring' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />Expiring Soon
            </span>
          )}
        </div>
      </div>

      <div className="flex items-end justify-between">
        <div>
          <p className="text-lg font-bold text-neutral-800 leading-none">
            {fmtQty(ing.currentQuantity)}
            <span className="text-xs font-normal text-neutral-400 ml-1">{ing.baseUnit}</span>
          </p>
          {ing.parLevel > 0 && (
            <p className="text-xs text-neutral-400 mt-0.5">Par: {fmtQty(ing.parLevel)}</p>
          )}
        </div>
        <div className="text-right">
          {ing.currentUnitCost > 0 && (
            <p className="text-xs font-semibold text-neutral-600">{fmtCurrency(ing.currentUnitCost)}<span className="font-normal text-neutral-400">/{ing.baseUnit}</span></p>
          )}
          {ing.inventoryValue > 0 && (
            <p className="text-xs text-neutral-400">{fmtCurrency(ing.inventoryValue)} total</p>
          )}
        </div>
      </div>

      {ing.parLevel > 0 && (
        <div className="w-full h-1.5 bg-neutral-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${cfg.dot}`}
            style={{ width: `${Math.min(100, (Number(ing.currentQuantity) / Number(ing.parLevel)) * 100)}%` }}
          />
        </div>
      )}

      <div className="flex gap-2 pt-1">
        <button
          onClick={() => onAdjust(ing)}
          className="flex-1 py-1.5 rounded-lg bg-brand-blue-light text-brand-blue text-xs font-semibold hover:bg-[#dde6f0] transition-colors"
        >
          Adjust
        </button>
        <button
          onClick={() => onEdit(ing)}
          className="flex-1 py-1.5 rounded-lg bg-neutral-50 text-neutral-600 text-xs font-medium hover:bg-neutral-100 transition-colors"
        >
          Edit
        </button>
      </div>
    </div>
  );
}

// ─── Ingredients Panel ────────────────────────────────────────────────────────

function IngredientsPanel({ ingredients, restaurantId, user }) {
  const [search,       setSearch]       = useState('');
  const [filterCat,    setFilterCat]    = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [editTarget,   setEditTarget]   = useState(null);   // null = closed, EMPTY means new
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [showAdd,      setShowAdd]      = useState(false);

  // Track expiry status per ingredient from inventoryLots
  // expiryMap: { [ingredientId]: 'expired' | 'expiring' | null }
  const [expiryMap, setExpiryMap] = useState({});
  useEffect(() => {
    if (!restaurantId) return;
    const today    = new Date(); today.setHours(0,0,0,0);
    const warnDate = new Date(today); warnDate.setDate(warnDate.getDate() + 2);
    const todayStr = today.toISOString().split('T')[0];
    const warnStr  = warnDate.toISOString().split('T')[0];

    const unsub = onSnapshot(
      query(collection(db, 'restaurants_data', restaurantId, 'inventoryLots')),
      snap => {
        const map = {};
        snap.docs.forEach(d => {
          const lot = d.data();
          if (lot.status === 'depleted') return;
          const { ingredientId, expiresAt } = lot;
          if (!ingredientId || !expiresAt) return;
          const current = map[ingredientId];
          if (expiresAt <= todayStr) {
            map[ingredientId] = 'expired';
          } else if (expiresAt <= warnStr && current !== 'expired') {
            map[ingredientId] = 'expiring';
          } else if (!current) {
            map[ingredientId] = null;
          }
        });
        setExpiryMap(map);
      },
      () => {},
    );
    return unsub;
  }, [restaurantId]);

  const filtered = ingredients.filter(ing => {
    if (!ing.active && filterStatus !== 'inactive') return false;
    if (filterStatus === 'inactive' && ing.active)  return false;
    if (filterCat && ing.category !== filterCat)    return false;
    if (filterStatus && filterStatus !== 'inactive' && stockStatus(ing) !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      return ing.name?.toLowerCase().includes(q) || ing.sku?.toLowerCase().includes(q) || ing.category?.toLowerCase().includes(q);
    }
    return true;
  });

  const totalValue = ingredients.filter(i => i.active).reduce((s, i) => s + (Number(i.inventoryValue) || 0), 0);
  const needsAttention = ingredients.filter(i => i.active && ['out','critical','low'].includes(stockStatus(i)));
  const expiryAlerts = ingredients.filter(i => i.active && expiryMap[i.id]);

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white border border-neutral-100 rounded-xl p-3 text-center shadow-sm">
          <p className="text-xl font-bold text-neutral-800">{ingredients.filter(i => i.active).length}</p>
          <p className="text-xs text-neutral-400">Ingredients</p>
        </div>
        <div className="bg-white border border-neutral-100 rounded-xl p-3 text-center shadow-sm">
          <p className="text-xl font-bold text-neutral-800">{fmtCurrency(totalValue)}</p>
          <p className="text-xs text-neutral-400">Total Value</p>
        </div>
        <div className={`border rounded-xl p-3 text-center shadow-sm ${needsAttention.length > 0 ? 'bg-red-50 border-red-100' : 'bg-white border-neutral-100'}`}>
          <p className={`text-xl font-bold ${needsAttention.length > 0 ? 'text-red-600' : 'text-neutral-800'}`}>{needsAttention.length}</p>
          <p className="text-xs text-neutral-400">Low Stock</p>
        </div>
        <div className={`border rounded-xl p-3 text-center shadow-sm ${expiryAlerts.length > 0 ? 'bg-orange-50 border-orange-100' : 'bg-white border-neutral-100'}`}>
          <p className={`text-xl font-bold ${expiryAlerts.length > 0 ? 'text-orange-600' : 'text-neutral-800'}`}>{expiryAlerts.length}</p>
          <p className="text-xs text-neutral-400">Expiring</p>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          className="flex-1 border border-neutral-200 rounded-xl px-3 py-2 text-sm"
          placeholder="Search ingredients…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          className="border border-neutral-200 rounded-xl px-3 py-2 text-sm text-neutral-600"
          value={filterCat}
          onChange={e => setFilterCat(e.target.value)}
        >
          <option value="">All Categories</option>
          {INGREDIENT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select
          className="border border-neutral-200 rounded-xl px-3 py-2 text-sm text-neutral-600"
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="out">Out</option>
          <option value="critical">Critical</option>
          <option value="low">Low</option>
          <option value="ok">OK</option>
          <option value="overstocked">Overstocked</option>
          <option value="unknown">Unknown</option>
          <option value="inactive">Inactive</option>
        </select>
        <button
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-brand-blue text-white text-sm font-semibold rounded-xl hover:bg-brand-blue-hover shrink-0"
        >
          + Add
        </button>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-neutral-400">
          <p className="text-sm">No ingredients found.</p>
          {ingredients.length === 0 && (
            <button onClick={() => setShowAdd(true)} className="mt-3 text-brand-blue text-sm font-medium hover:underline">
              Add your first ingredient
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(ing => (
            <IngredientCard
              key={ing.id}
              ing={ing}
              onEdit={i => setEditTarget(i)}
              onAdjust={i => setAdjustTarget(i)}
              expiryStatus={expiryMap[ing.id] || null}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showAdd && (
        <IngredientModal
          ingredient={null}
          restaurantId={restaurantId}
          onClose={() => setShowAdd(false)}
          onSaved={() => setShowAdd(false)}
        />
      )}
      {editTarget && (
        <IngredientModal
          ingredient={editTarget}
          restaurantId={restaurantId}
          onClose={() => setEditTarget(null)}
          onSaved={() => setEditTarget(null)}
        />
      )}
      {adjustTarget && (
        <AdjustStockModal
          ingredient={adjustTarget}
          restaurantId={restaurantId}
          user={user}
          onClose={() => setAdjustTarget(null)}
        />
      )}
    </div>
  );
}

// ─── Count Panel ─────────────────────────────────────────────────────────────

function CountPanel({ ingredients, restaurantId, user }) {
  const countable = ingredients.filter(i => i.active && i.countingEnabled !== false);
  const sorted    = [...countable].sort((a, b) => (Number(a.countingOrder) || 0) - (Number(b.countingOrder) || 0));

  const [counts,   setCounts]   = useState({});
  const [notes,    setNotes]    = useState({});
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [error,    setError]    = useState('');

  const handleSubmit = async () => {
    const entries = sorted.filter(i => counts[i.id] !== '' && counts[i.id] != null && !isNaN(parseFloat(counts[i.id])));
    if (entries.length === 0) { setError('Enter at least one count.'); return; }
    setSaving(true);
    setError('');

    try {
      const batch = writeBatch(db);
      const movRefs = entries.map(() => doc(collection(db, 'restaurants_data', restaurantId, 'inventoryMovements')));

      entries.forEach((ing, idx) => {
        const prev  = Number(ing.currentQuantity) || 0;
        const next  = parseFloat(counts[ing.id]);
        const delta = next - prev;
        const cost  = Number(ing.currentUnitCost) || 0;
        const note  = notes[ing.id] || '';

        batch.set(movRefs[idx], {
          ingredientId:     ing.id,
          ingredientName:   ing.name,
          ingredientUnit:   ing.baseUnit,
          type:             'count',
          quantity:         delta,
          previousQuantity: prev,
          newQuantity:      next,
          timestamp:        serverTimestamp(),
          employeeId:       user.accountId,
          employeeName:     `${user.firstName} ${user.lastName}`,
          source:           'count_sheet',
          notes:            note,
        });

        const ingRef = doc(db, 'restaurants_data', restaurantId, 'ingredients', ing.id);
        batch.update(ingRef, {
          currentQuantity:  next,
          inventoryValue:   next * cost,
          lastCountedAt:    serverTimestamp(),
          lastCountedBy:    user.accountId,
          actualRemaining:  next,
          variance:         delta,
          updatedAt:        serverTimestamp(),
        });
      });

      await batch.commit();
      setCounts({});
      setNotes({});
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-neutral-700">Physical Count Sheet</h3>
          <p className="text-xs text-neutral-400">{sorted.length} countable ingredient{sorted.length !== 1 ? 's' : ''}</p>
        </div>
        {saved && <span className="text-xs text-green-600 font-medium">Count saved!</span>}
      </div>

      {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}

      {sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-neutral-400">
          <p className="text-sm">No countable ingredients.</p>
          <p className="text-xs mt-1">Enable counting on ingredients in the Ingredients tab.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {sorted.map(ing => {
              const val    = counts[ing.id] ?? '';
              const hasVal = val !== '' && !isNaN(parseFloat(val));
              const delta  = hasVal ? parseFloat(val) - (Number(ing.currentQuantity) || 0) : null;

              let inputCls = 'border-neutral-200';
              let deltaCls = '';
              if (hasVal && delta !== null) {
                if (delta < 0)      { inputCls = 'border-red-200 bg-red-50 text-red-700';   deltaCls = 'text-red-500'; }
                else if (delta > 0) { inputCls = 'border-blue-200 bg-blue-50 text-blue-700'; deltaCls = 'text-blue-500'; }
                else                { inputCls = 'border-green-200 bg-green-50 text-green-700'; }
              }

              return (
                <div key={ing.id} className="bg-white border border-neutral-100 rounded-xl p-3 flex flex-col gap-2.5">
                  {/* Name + unit */}
                  <div>
                    <p className="text-sm font-semibold text-neutral-800 leading-tight">{ing.name}</p>
                    <p className="text-xs text-neutral-400 mt-0.5">{ing.baseUnit}</p>
                  </div>

                  {/* On-hand chip */}
                  <div className="flex items-baseline gap-1 bg-neutral-50 rounded-lg px-2.5 py-1.5">
                    <span className="text-base font-bold text-neutral-700 leading-none">{fmtQty(ing.currentQuantity)}</span>
                    <span className="text-[10px] text-neutral-400">on hand</span>
                  </div>

                  {/* Count input */}
                  <div>
                    <label className="block text-[10px] font-medium text-neutral-400 mb-1">Count</label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      className={`w-full border rounded-lg px-2 py-2 text-sm text-center font-medium ${inputCls}`}
                      placeholder="—"
                      value={val}
                      onChange={e => setCounts(c => ({ ...c, [ing.id]: e.target.value }))}
                    />
                    {hasVal && delta !== null && delta !== 0 && (
                      <p className={`text-[10px] text-center mt-1 font-semibold ${deltaCls}`}>
                        {delta > 0 ? '+' : ''}{fmtQty(delta)} variance
                      </p>
                    )}
                  </div>

                  {/* Note */}
                  <input
                    className="w-full border border-neutral-200 rounded-lg px-2 py-1.5 text-xs text-neutral-600 placeholder:text-neutral-300"
                    placeholder="Note…"
                    value={notes[ing.id] || ''}
                    onChange={e => setNotes(n => ({ ...n, [ing.id]: e.target.value }))}
                  />
                </div>
              );
            })}
          </div>

          <div className="flex justify-end pt-2">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-6 py-2.5 bg-brand-blue text-white text-sm font-semibold rounded-xl hover:bg-brand-blue-hover disabled:opacity-50"
            >
              {saving ? 'Submitting…' : 'Submit Count'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Log Movement Modal ───────────────────────────────────────────────────────

function LogMovementModal({ ingredients, restaurantId, user, onClose }) {
  const [ingId,          setIngId]          = useState('');
  const [type,           setType]           = useState('received');
  const [qty,            setQty]            = useState('');
  const [notes,          setNotes]          = useState('');
  const [wasteReason,    setWasteReason]    = useState('');
  const [lotDate,        setLotDate]        = useState('');
  const [saving,         setSaving]         = useState(false);
  const [error,          setError]          = useState('');
  const [suggest86Items, setSuggest86Items] = useState(null);

  const ing     = ingredients.find(i => i.id === ingId);
  const movType = MOVEMENT_TYPES.find(m => m.value === type);

  const handleSave = async () => {
    if (!ingId) { setError('Select an ingredient.'); return; }
    const n = parseFloat(qty);
    if (!qty || isNaN(n) || n === 0) { setError('Enter a valid quantity.'); return; }
    if (type === 'waste' && !wasteReason) { setError('Select a waste reason.'); return; }
    setSaving(true);
    setError('');
    try {
      const delta = n * movType.sign;
      await writeMovement(restaurantId, ing, type, delta, user, notes.trim(), wasteReason, lotDate || null);
      const newQty = (Number(ing.currentQuantity) || 0) + delta;
      const min    = Number(ing.minimumQuantity) || 0;
      if (newQty <= min) {
        const affected = await findAffectedItems(restaurantId, ing.id);
        if (affected.length > 0) {
          setSuggest86Items({ ing, affected });
          return;
        }
      }
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (suggest86Items) {
    return (
      <Suggest86Modal
        ingredientName={suggest86Items.ing.name}
        affectedItems={suggest86Items.affected}
        restaurantId={restaurantId}
        onDone={onClose}
      />
    );
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[85dvh]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="shrink-0 flex items-center justify-between px-4 pt-4 pb-3 border-b border-neutral-100">
          <h2 className="text-base font-semibold text-neutral-800">Log Movement</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">&times;</button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}

          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Ingredient</label>
            <select
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
              value={ingId}
              onChange={e => setIngId(e.target.value)}
            >
              <option value="">Select ingredient…</option>
              {ingredients.filter(i => i.active).map(i => (
                <option key={i.id} value={i.id}>{i.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Type</label>
            <div className="grid grid-cols-2 gap-2">
              {MOVEMENT_TYPES.map(m => (
                <button
                  key={m.value}
                  onClick={() => setType(m.value)}
                  className={`py-2 rounded-lg text-sm font-medium border transition-colors ${
                    type === m.value
                      ? 'bg-brand-blue-light border-brand-blue text-brand-blue'
                      : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">
              Quantity {ing ? `(${ing.baseUnit})` : ''}
            </label>
            <input
              type="number" min="0" step="any"
              className="w-full border border-neutral-200 rounded-lg px-3 py-2.5 text-sm"
              value={qty}
              onChange={e => setQty(e.target.value)}
              placeholder="0"
            />
          </div>

          {type === 'waste' && (
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">Waste Reason <span className="text-red-400">*</span></label>
              <div className="grid grid-cols-2 gap-1.5">
                {WASTE_REASONS.map(r => (
                  <button
                    key={r.value}
                    onClick={() => setWasteReason(r.value)}
                    className={`py-2 px-2 rounded-lg text-xs font-medium border text-left transition-colors ${
                      wasteReason === r.value
                        ? 'bg-red-50 border-red-300 text-red-700'
                        : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {type === 'received' && ing?.shelfLifeDays && (
            <div>
              <label className="block text-xs font-medium text-neutral-500 mb-1">
                Received Date <span className="text-neutral-300">(for expiry tracking)</span>
              </label>
              <input
                type="date"
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                value={lotDate}
                onChange={e => setLotDate(e.target.value)}
              />
              {lotDate && (
                <p className="text-xs text-neutral-400 mt-1">
                  Expires: {(() => {
                    const d = new Date(lotDate);
                    d.setDate(d.getDate() + Number(ing.shelfLifeDays));
                    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  })()}
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Notes (optional)</label>
            <input
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Additional details…"
            />
          </div>
        </div>

        <div className="shrink-0 flex gap-2 px-4 py-3 border-t border-neutral-100">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-neutral-200 text-sm font-medium text-neutral-600 hover:bg-neutral-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-hover disabled:opacity-50">
            {saving ? 'Saving…' : 'Log'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ─── Movement row ─────────────────────────────────────────────────────────────

const MOV_TYPE_COLORS = {
  received:   'text-green-600 bg-green-50',
  waste:      'text-red-600 bg-red-50',
  adjustment: 'text-blue-600 bg-blue-50',
  transfer:   'text-brand-blue bg-brand-blue-light',
  depletion:  'text-purple-600 bg-purple-50',
  count:      'text-neutral-600 bg-neutral-100',
};

function MovementRow({ mov }) {
  const colorClass = MOV_TYPE_COLORS[mov.type] || MOV_TYPE_COLORS.count;
  const deltaSign  = mov.quantity >= 0 ? '+' : '';

  return (
    <div className="flex items-center gap-3 bg-white border border-neutral-100 rounded-xl px-3 py-2.5">
      <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-lg shrink-0 ${colorClass}`}>
        {mov.type}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-neutral-800 truncate">{mov.ingredientName}</p>
        <p className="text-xs text-neutral-400 truncate">
          {mov.employeeName}
          {mov.wasteReason ? ` · ${WASTE_REASONS.find(r => r.value === mov.wasteReason)?.label || mov.wasteReason}` : ''}
          {mov.notes ? ` · ${mov.notes}` : ''}
        </p>
      </div>
      <div className="text-right shrink-0">
        <p className={`text-sm font-semibold ${mov.quantity < 0 ? 'text-red-500' : 'text-green-600'}`}>
          {deltaSign}{fmtQty(mov.quantity)} {mov.ingredientUnit}
        </p>
        <p className="text-xs text-neutral-400">
          {mov.timestamp ? fmtDate(mov.timestamp) : '—'}
        </p>
      </div>
    </div>
  );
}

// ─── Movements Panel ──────────────────────────────────────────────────────────

function MovementsPanel({ ingredients, restaurantId, user }) {
  const [movements, setMovements] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showLog,   setShowLog]   = useState(false);
  const [filterType, setFilterType] = useState('');
  const unsubRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, 'restaurants_data', restaurantId, 'inventoryMovements'),
      orderBy('timestamp', 'desc'),
      limit(200),
    );
    unsubRef.current = onSnapshot(q, snap => {
      setMovements(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));

    return () => { if (unsubRef.current) unsubRef.current(); };
  }, [restaurantId]);

  const filtered = filterType
    ? movements.filter(m => m.type === filterType)
    : movements;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-neutral-700">Movement Log</h3>
          <p className="text-xs text-neutral-400">Last 200 movements</p>
        </div>
        <div className="flex gap-2">
          <select
            className="border border-neutral-200 rounded-xl px-3 py-2 text-sm text-neutral-600"
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
          >
            <option value="">All Types</option>
            {MOVEMENT_TYPES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            <option value="count">Count</option>
          </select>
          <button
            onClick={() => setShowLog(true)}
            className="px-4 py-2 bg-brand-blue text-white text-sm font-semibold rounded-xl hover:bg-brand-blue-hover shrink-0"
          >
            + Log
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-brand-blue border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-neutral-400">
          <p className="text-sm">No movements recorded yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(m => <MovementRow key={m.id} mov={m} />)}
        </div>
      )}

      {showLog && (
        <LogMovementModal
          ingredients={ingredients}
          restaurantId={restaurantId}
          user={user}
          onClose={() => setShowLog(false)}
        />
      )}
    </div>
  );
}

// ─── Purchase Orders ──────────────────────────────────────────────────────────

const PO_STATUSES = [
  { id: 'draft',     label: 'Draft',     color: 'bg-neutral-100 text-neutral-500' },
  { id: 'submitted', label: 'Submitted', color: 'bg-blue-100 text-blue-700' },
  { id: 'partial',   label: 'Partial',   color: 'bg-yellow-100 text-yellow-700' },
  { id: 'received',  label: 'Received',  color: 'bg-green-100 text-green-700' },
];

function poStatusConfig(status) {
  return PO_STATUSES.find(s => s.id === status) || PO_STATUSES[0];
}

// ─── Create / Edit PO Modal ───────────────────────────────────────────────────

function PurchaseOrderModal({ po, ingredients, restaurantId, user, onClose, onSaved }) {
  const isNew = !po?.id;
  const [supplierName, setSupplierName] = useState(po?.supplierName || '');
  const [notes, setNotes]               = useState(po?.notes || '');
  const [lines, setLines]               = useState(
    po?.items?.length ? po.items.map(i => ({ ...i })) : [{ ingredientId: '', name: '', orderedQty: '', unit: '' }]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const activeIngs = ingredients.filter(i => i.active !== false);

  const setLine = (idx, field, value) => {
    setLines(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const pickIngredient = (idx, ingId) => {
    const ing = activeIngs.find(i => i.id === ingId);
    if (!ing) { setLine(idx, 'ingredientId', ''); return; }
    setLines(prev => prev.map((l, i) => i === idx ? {
      ...l,
      ingredientId: ingId,
      name: ing.name,
      unit: ing.baseUnit || ing.unit || 'each',
    } : l));
  };

  const addLine = () => setLines(prev => [...prev, { ingredientId: '', name: '', orderedQty: '', unit: '' }]);
  const removeLine = (idx) => setLines(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async (status = 'draft') => {
    setError('');
    const validLines = lines.filter(l => l.ingredientId && Number(l.orderedQty) > 0);
    if (!supplierName.trim()) { setError('Supplier name is required.'); return; }
    if (validLines.length === 0) { setError('Add at least one ingredient with a quantity.'); return; }

    setSaving(true);
    try {
      const poRef = isNew
        ? doc(collection(db, 'restaurants_data', restaurantId, 'purchaseOrders'))
        : doc(db, 'restaurants_data', restaurantId, 'purchaseOrders', po.id);

      const payload = {
        supplierName: supplierName.trim(),
        notes: notes.trim(),
        items: validLines.map(l => ({
          ingredientId: l.ingredientId,
          name: l.name,
          unit: l.unit,
          orderedQty: Number(l.orderedQty),
          receivedQty: l.receivedQty ?? 0,
        })),
        status,
        updatedAt: serverTimestamp(),
        updatedBy: user?.accountId || '',
      };

      if (isNew) {
        payload.createdAt = serverTimestamp();
        payload.createdBy = user?.accountId || '';
        payload.createdByName = user ? `${user.firstName} ${user.lastName}`.trim() : '';
      }

      await setDoc(poRef, payload, { merge: true });
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[90dvh]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="shrink-0 flex items-center justify-between px-4 pt-4 pb-3 border-b border-neutral-100">
          <h2 className="text-base font-semibold text-neutral-800">
            {isNew ? 'New Purchase Order' : 'Edit Purchase Order'}
          </h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">&times;</button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}

          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Supplier / Vendor</label>
            <input
              className="w-full border border-neutral-200 rounded-lg px-3 py-2.5 text-sm"
              value={supplierName}
              onChange={e => setSupplierName(e.target.value)}
              placeholder="e.g. Sysco, US Foods…"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Notes (optional)</label>
            <input
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Delivery date, special instructions…"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-neutral-500">Items to Order</label>
              <button onClick={addLine} className="text-xs font-semibold text-brand-blue hover:underline">+ Add Item</button>
            </div>
            <div className="space-y-2">
              {lines.map((line, idx) => (
                <div key={idx} className="flex gap-2 items-center">
                  <select
                    className="flex-1 border border-neutral-200 rounded-lg px-2 py-2 text-sm min-w-0"
                    value={line.ingredientId}
                    onChange={e => pickIngredient(idx, e.target.value)}
                  >
                    <option value="">Select ingredient…</option>
                    {activeIngs.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                  <input
                    type="number" min="0" step="any"
                    className="w-20 border border-neutral-200 rounded-lg px-2 py-2 text-sm"
                    value={line.orderedQty}
                    onChange={e => setLine(idx, 'orderedQty', e.target.value)}
                    placeholder="Qty"
                  />
                  <span className="text-xs text-neutral-400 shrink-0 w-10 truncate">{line.unit || ''}</span>
                  {lines.length > 1 && (
                    <button onClick={() => removeLine(idx)} className="text-neutral-300 hover:text-red-400 text-lg leading-none shrink-0">&times;</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="shrink-0 flex gap-2 px-4 py-3 border-t border-neutral-100">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-neutral-200 text-sm font-medium text-neutral-600 hover:bg-neutral-50">
            Cancel
          </button>
          <button onClick={() => handleSave('draft')} disabled={saving} className="py-2.5 px-4 rounded-xl border border-neutral-200 text-sm font-medium text-neutral-600 hover:bg-neutral-50 disabled:opacity-50">
            Save Draft
          </button>
          <button onClick={() => handleSave('submitted')} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-hover disabled:opacity-50">
            {saving ? 'Saving…' : 'Submit Order'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ─── Receive Items Modal ──────────────────────────────────────────────────────

function ReceiveItemsModal({ po, restaurantId, user, onClose, onReceived }) {
  const [receivedQtys, setReceivedQtys] = useState(
    Object.fromEntries((po.items || []).map(item => [item.ingredientId, String(item.receivedQty || 0)]))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const batch = writeBatch(db);
      const poRef = doc(db, 'restaurants_data', restaurantId, 'purchaseOrders', po.id);

      const updatedItems = (po.items || []).map(item => {
        const prevReceived = Number(item.receivedQty) || 0;
        const newReceived  = Number(receivedQtys[item.ingredientId]) || 0;
        const delta        = newReceived - prevReceived;

        if (delta !== 0) {
          const ingRef = doc(db, 'restaurants_data', restaurantId, 'ingredients', item.ingredientId);
          batch.update(ingRef, {
            currentQuantity: increment(delta),
            lastReceivedDate: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });

          // Log movement
          const movRef = doc(collection(db, 'restaurants_data', restaurantId, 'inventoryMovements'));
          batch.set(movRef, {
            ingredientId:     item.ingredientId,
            ingredientName:   item.name,
            ingredientUnit:   item.unit,
            type:             'received',
            quantity:         delta,
            previousQuantity: null,
            newQuantity:      null,
            timestamp:        serverTimestamp(),
            employeeId:       user?.accountId || '',
            employeeName:     user ? `${user.firstName} ${user.lastName}`.trim() : '',
            source:           'purchase_order',
            notes:            `PO from ${po.supplierName}`,
          });
        }

        return { ...item, receivedQty: newReceived };
      });

      const allReceived = updatedItems.every(
        i => Number(i.receivedQty) >= Number(i.orderedQty)
      );
      const anyReceived = updatedItems.some(i => Number(i.receivedQty) > 0);
      const newStatus = allReceived ? 'received' : anyReceived ? 'partial' : po.status;

      batch.update(poRef, {
        items: updatedItems,
        status: newStatus,
        updatedAt: serverTimestamp(),
        updatedBy: user?.accountId || '',
      });

      await batch.commit();
      onReceived?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[90dvh]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="shrink-0 flex items-center justify-between px-4 pt-4 pb-3 border-b border-neutral-100">
          <div>
            <h2 className="text-base font-semibold text-neutral-800">Receive Items</h2>
            <p className="text-xs text-neutral-400">{po.supplierName}</p>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">&times;</button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
          <p className="text-xs text-neutral-400">Enter the quantity actually received. Inventory will be updated by the difference from what was previously recorded.</p>

          {(po.items || []).map(item => (
            <div key={item.ingredientId} className="flex items-center gap-3 bg-neutral-50 rounded-xl px-3 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-neutral-800 truncate">{item.name}</p>
                <p className="text-xs text-neutral-400">Ordered: {fmtQty(item.orderedQty)} {item.unit}</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <input
                  type="number" min="0" step="any"
                  className="w-20 border border-neutral-200 rounded-lg px-2 py-2 text-sm text-right"
                  value={receivedQtys[item.ingredientId] ?? ''}
                  onChange={e => setReceivedQtys(prev => ({ ...prev, [item.ingredientId]: e.target.value }))}
                />
                <span className="text-xs text-neutral-400">{item.unit}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="shrink-0 flex gap-2 px-4 py-3 border-t border-neutral-100">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-neutral-200 text-sm font-medium text-neutral-600 hover:bg-neutral-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50">
            {saving ? 'Saving…' : 'Confirm Receipt'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ─── PO Row ───────────────────────────────────────────────────────────────────

function PurchaseOrderRow({ po, onEdit, onReceive }) {
  const sc = poStatusConfig(po.status);
  const totalItems = (po.items || []).length;
  const receivedItems = (po.items || []).filter(i => Number(i.receivedQty) >= Number(i.orderedQty)).length;

  return (
    <div className="bg-white border border-neutral-100 rounded-xl px-4 py-3 space-y-2">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-neutral-800 truncate">{po.supplierName}</p>
          <p className="text-xs text-neutral-400">
            {fmtDate(po.createdAt)} · {totalItems} item{totalItems !== 1 ? 's' : ''}
            {po.status !== 'draft' && ` · ${receivedItems}/${totalItems} received`}
          </p>
          {po.notes && <p className="text-xs text-neutral-400 truncate mt-0.5">{po.notes}</p>}
        </div>
        <span className={`text-[10px] font-semibold uppercase px-2 py-1 rounded-lg shrink-0 ${sc.color}`}>
          {sc.label}
        </span>
      </div>

      {/* Item list (collapsed summary) */}
      <div className="space-y-1">
        {(po.items || []).map(item => {
          const received = Number(item.receivedQty) || 0;
          const ordered  = Number(item.orderedQty)  || 0;
          const done     = received >= ordered;
          return (
            <div key={item.ingredientId} className="flex items-center gap-2 text-xs">
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${done ? 'bg-green-400' : 'bg-neutral-300'}`} />
              <span className="flex-1 text-neutral-600 truncate">{item.name}</span>
              <span className="text-neutral-400 shrink-0">
                {po.status !== 'draft'
                  ? `${fmtQty(received)} / ${fmtQty(ordered)} ${item.unit}`
                  : `${fmtQty(ordered)} ${item.unit}`
                }
              </span>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-1">
        {(po.status === 'draft' || po.status === 'submitted') && (
          <button
            onClick={onEdit}
            className="flex-1 py-1.5 rounded-lg border border-neutral-200 text-xs font-medium text-neutral-600 hover:bg-neutral-50"
          >
            Edit
          </button>
        )}
        {(po.status === 'submitted' || po.status === 'partial') && (
          <button
            onClick={onReceive}
            className="flex-1 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700"
          >
            Receive Items
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Purchase Orders Panel ────────────────────────────────────────────────────

// Group reorder suggestions by supplier and open a pre-filled PO modal
function buildSuggestedPO(suggestions) {
  // suggestions: [{ ingredient, suggestedQty }]
  // Group by defaultSupplierId / supplierName for a realistic PO
  const groups = {};
  for (const { ingredient, suggestedQty } of suggestions) {
    const key = ingredient.defaultSupplierId || ingredient.supplierName || '__none__';
    if (!groups[key]) groups[key] = { supplierName: ingredient.supplierName || '', lines: [] };
    groups[key].lines.push({
      ingredientId: ingredient.id,
      name:         ingredient.name,
      unit:         ingredient.baseUnit || 'each',
      orderedQty:   String(suggestedQty),
      receivedQty:  0,
    });
  }
  return groups;
}

function PurchaseOrdersPanel({ ingredients, restaurantId, user }) {
  const [orders, setOrders]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editPo, setEditPo]       = useState(null);
  const [receivePo, setReceivePo] = useState(null);
  const [statusFilter, setStatusFilter] = useState('active');

  // Compute reorder suggestions — ingredients at or below par with a default supplier
  const suggestions = ingredients
    .filter(i => {
      if (!i.active) return false;
      const status = stockStatus(i);
      return (status === 'out' || status === 'critical' || status === 'low') && i.parLevel > 0;
    })
    .map(i => ({
      ingredient:   i,
      suggestedQty: Math.max(0, (Number(i.parLevel) || 0) - (Number(i.currentQuantity) || 0)),
    }))
    .filter(s => s.suggestedQty > 0);

  // Build a pre-filled PO from suggestions, grouped by first supplier
  const [suggestionPo, setSuggestionPo] = useState(null);

  const createPoFromSuggestions = (subset) => {
    const groups = buildSuggestedPO(subset);
    const firstGroup = Object.values(groups)[0];
    if (!firstGroup) return;
    setSuggestionPo({
      supplierName: firstGroup.supplierName,
      notes: 'Auto-generated reorder suggestion',
      items: firstGroup.lines,
    });
  };

  useEffect(() => {
    setLoading(true);
    const q = query(
      collection(db, 'restaurants_data', restaurantId, 'purchaseOrders'),
      orderBy('createdAt', 'desc'),
      limit(100),
    );
    const unsub = onSnapshot(q, snap => {
      setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [restaurantId]);

  const filtered = orders.filter(o => {
    if (statusFilter === 'active') return o.status === 'draft' || o.status === 'submitted' || o.status === 'partial';
    if (statusFilter === 'received') return o.status === 'received';
    return true;
  });

  return (
    <div className="space-y-4">
      {/* Reorder suggestions banner */}
      {suggestions.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-amber-800">
                {suggestions.length} ingredient{suggestions.length !== 1 ? 's' : ''} need reordering
              </p>
              <p className="text-xs text-amber-600">Below par level — suggested order quantities to reach par</p>
            </div>
            <button
              onClick={() => createPoFromSuggestions(suggestions)}
              className="shrink-0 px-3 py-1.5 bg-amber-600 text-white text-xs font-semibold rounded-lg hover:bg-amber-700"
            >
              Create PO
            </button>
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {suggestions.map(({ ingredient, suggestedQty }) => {
              const sc = STATUS_CONFIG[stockStatus(ingredient)];
              return (
                <div key={ingredient.id} className="flex items-center gap-2 text-xs">
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${sc.dot}`} />
                  <span className="flex-1 text-neutral-700 truncate font-medium">{ingredient.name}</span>
                  <span className="text-neutral-500 shrink-0">
                    {fmtQty(ingredient.currentQuantity)} on hand
                  </span>
                  <span className="text-amber-700 font-semibold shrink-0">
                    order +{fmtQty(suggestedQty)} {ingredient.baseUnit}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-neutral-700">Purchase Orders</h3>
          <p className="text-xs text-neutral-400">Create shopping lists and record deliveries</p>
        </div>
        <div className="flex gap-2">
          <select
            className="border border-neutral-200 rounded-xl px-3 py-2 text-sm text-neutral-600"
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
          >
            <option value="active">Active</option>
            <option value="received">Received</option>
            <option value="all">All</option>
          </select>
          <button
            onClick={() => setShowCreate(true)}
            className="px-4 py-2 bg-brand-blue text-white text-sm font-semibold rounded-xl hover:bg-brand-blue-hover shrink-0"
          >
            + New PO
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-brand-blue border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-neutral-400">
          <p className="text-sm">No purchase orders yet.</p>
          <button onClick={() => setShowCreate(true)} className="mt-3 text-sm text-brand-blue font-medium hover:underline">
            Create your first PO
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(po => (
            <PurchaseOrderRow
              key={po.id}
              po={po}
              onEdit={() => setEditPo(po)}
              onReceive={() => setReceivePo(po)}
            />
          ))}
        </div>
      )}

      {(showCreate || editPo) && (
        <PurchaseOrderModal
          po={editPo || null}
          ingredients={ingredients}
          restaurantId={restaurantId}
          user={user}
          onClose={() => { setShowCreate(false); setEditPo(null); }}
          onSaved={() => {}}
        />
      )}

      {suggestionPo && (
        <PurchaseOrderModal
          po={suggestionPo}
          ingredients={ingredients}
          restaurantId={restaurantId}
          user={user}
          onClose={() => setSuggestionPo(null)}
          onSaved={() => setSuggestionPo(null)}
        />
      )}

      {receivePo && (
        <ReceiveItemsModal
          po={receivePo}
          restaurantId={restaurantId}
          user={user}
          onClose={() => setReceivePo(null)}
          onReceived={() => {}}
        />
      )}
    </div>
  );
}

// ─── COGS Panel ───────────────────────────────────────────────────────────────

function COGSPanel({ ingredients, restaurantId }) {
  const [menuItems, setMenuItems] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [search,    setSearch]    = useState('');

  useEffect(() => {
    setLoading(true);
    getDocs(collection(db, 'restaurants_data', restaurantId, 'items'))
      .then(snap => { setMenuItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); })
      .catch(() => setLoading(false));
  }, [restaurantId]);

  // Build ingredient cost map for fast lookup
  const costMap = Object.fromEntries(
    ingredients.map(i => [i.id, Number(i.currentUnitCost) || 0])
  );

  const costedItems = menuItems
    .filter(item => item.ingredients?.length > 0)
    .map(item => {
      const recipeCost = (item.ingredients || []).reduce((sum, ing) => {
        const unitCost = costMap[ing.ingredientId] || 0;
        return sum + unitCost * (Number(ing.quantity) || 0);
      }, 0);
      const price      = Number(item.price) || 0;
      const foodCostPct = price > 0 ? (recipeCost / price) * 100 : null;
      return { ...item, recipeCost, foodCostPct };
    })
    .sort((a, b) => (b.foodCostPct ?? -1) - (a.foodCostPct ?? -1));

  const filtered = costedItems.filter(i =>
    !search || i.name?.toLowerCase().includes(search.toLowerCase())
  );

  const uncostedCount = menuItems.filter(i => !i.ingredients?.length).length;
  const avgFoodCost   = costedItems.filter(i => i.foodCostPct != null).length > 0
    ? costedItems.filter(i => i.foodCostPct != null).reduce((s, i) => s + i.foodCostPct, 0) /
      costedItems.filter(i => i.foodCostPct != null).length
    : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-neutral-700">Recipe Costing & COGS</h3>
          <p className="text-xs text-neutral-400">
            {costedItems.length} costed item{costedItems.length !== 1 ? 's' : ''}
            {uncostedCount > 0 && ` · ${uncostedCount} without recipe`}
          </p>
        </div>
        {avgFoodCost != null && (
          <div className={`px-3 py-1.5 rounded-xl text-sm font-semibold ${
            avgFoodCost > 35 ? 'bg-red-50 text-red-700' : avgFoodCost > 28 ? 'bg-yellow-50 text-yellow-700' : 'bg-green-50 text-green-700'
          }`}>
            Avg food cost: {avgFoodCost.toFixed(1)}%
          </div>
        )}
      </div>

      <input
        className="w-full border border-neutral-200 rounded-xl px-3 py-2 text-sm"
        placeholder="Search items…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-brand-blue border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-neutral-400">
          <p className="text-sm">No costed items found.</p>
          <p className="text-xs mt-1">Add ingredient recipes to menu items in Edit Items.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(item => {
            const pct = item.foodCostPct;
            const pctColor = pct == null ? 'text-neutral-400'
              : pct > 35 ? 'text-red-600' : pct > 28 ? 'text-yellow-600' : 'text-green-600';
            const pctBg = pct == null ? 'bg-neutral-50'
              : pct > 35 ? 'bg-red-50' : pct > 28 ? 'bg-yellow-50' : 'bg-green-50';

            return (
              <div key={item.id} className="bg-white border border-neutral-100 rounded-xl p-3 flex flex-col gap-2 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-semibold text-neutral-800 leading-tight truncate">{item.name}</p>
                  {item.category && <p className="text-xs text-neutral-400 shrink-0">{item.category}</p>}
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex-1 space-y-0.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-neutral-500">Recipe cost</span>
                      <span className="font-semibold text-neutral-700">{fmtCurrency(item.recipeCost)}</span>
                    </div>
                    {Number(item.price) > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-neutral-500">Menu price</span>
                        <span className="font-semibold text-neutral-700">{fmtCurrency(item.price)}</span>
                      </div>
                    )}
                    {Number(item.price) > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-neutral-500">Gross margin</span>
                        <span className="font-semibold text-green-600">{fmtCurrency(Number(item.price) - item.recipeCost)}</span>
                      </div>
                    )}
                  </div>
                  {pct != null && (
                    <div className={`flex flex-col items-center justify-center w-14 h-14 rounded-xl shrink-0 ${pctBg}`}>
                      <span className={`text-base font-bold leading-none ${pctColor}`}>{pct.toFixed(0)}%</span>
                      <span className="text-[9px] text-neutral-400 mt-0.5">food cost</span>
                    </div>
                  )}
                </div>

                {/* Ingredient breakdown */}
                <div className="space-y-0.5 pt-1 border-t border-neutral-50">
                  {(item.ingredients || []).map((ing, i) => {
                    const unitCost = costMap[ing.ingredientId] || 0;
                    const lineCost = unitCost * (Number(ing.quantity) || 0);
                    return (
                      <div key={i} className="flex justify-between text-xs text-neutral-400">
                        <span className="truncate">{ing.name} × {ing.quantity} {ing.unit}</span>
                        <span className="shrink-0 ml-2">{fmtCurrency(lineCost)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {uncostedCount > 0 && (
        <p className="text-xs text-neutral-400 text-center pt-2">
          {uncostedCount} item{uncostedCount !== 1 ? 's' : ''} have no ingredient recipe — add ingredients in Edit Items to see their food cost.
        </p>
      )}
    </div>
  );
}

// ─── Supplier Modal ───────────────────────────────────────────────────────────

const EMPTY_SUPPLIER = {
  name: '', contactName: '', phone: '', email: '', website: '', notes: '', isActive: true,
};

function SupplierModal({ supplier, restaurantId, onClose, onSaved }) {
  const isNew = !supplier;
  const [form, setForm] = useState(supplier ? { ...EMPTY_SUPPLIER, ...supplier } : { ...EMPTY_SUPPLIER });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const ref = isNew
        ? doc(collection(db, 'restaurants_data', restaurantId, 'suppliers'))
        : doc(db, 'restaurants_data', restaurantId, 'suppliers', supplier.id);
      await setDoc(ref, { ...form, updatedAt: serverTimestamp(), ...(isNew ? { createdAt: serverTimestamp() } : {}) }, { merge: true });
      onSaved?.();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <ModalOverlay onClose={onClose}>
      <div className="w-full sm:max-w-sm bg-white rounded-t-2xl sm:rounded-2xl flex flex-col max-h-[90dvh]" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="shrink-0 flex items-center justify-between px-4 pt-4 pb-3 border-b border-neutral-100">
          <h2 className="text-base font-semibold text-neutral-800">{isNew ? 'Add Supplier' : 'Edit Supplier'}</h2>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl leading-none">&times;</button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {error && <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>}
          {[
            { field: 'name',        label: 'Company Name *', placeholder: 'e.g. Sysco' },
            { field: 'contactName', label: 'Contact Name',   placeholder: 'Sales rep name' },
            { field: 'phone',       label: 'Phone',          placeholder: '+1 (555) 000-0000' },
            { field: 'email',       label: 'Email',          placeholder: 'orders@supplier.com' },
            { field: 'website',     label: 'Website',        placeholder: 'https://…' },
          ].map(({ field, label, placeholder }) => (
            <div key={field}>
              <label className="block text-xs font-medium text-neutral-500 mb-1">{label}</label>
              <input
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm"
                value={form[field]}
                onChange={e => set(field, e.target.value)}
                placeholder={placeholder}
              />
            </div>
          ))}
          <div>
            <label className="block text-xs font-medium text-neutral-500 mb-1">Notes</label>
            <textarea
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm resize-none"
              rows={2}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Delivery schedule, min order, etc."
            />
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-neutral-700">Active</span>
            <button type="button" onClick={() => set('isActive', !form.isActive)}
              className={`w-10 h-6 rounded-full transition-colors ${form.isActive ? 'bg-brand-blue' : 'bg-neutral-200'}`}>
              <span className={`block w-4 h-4 rounded-full bg-white shadow mx-1 transition-transform ${form.isActive ? 'translate-x-4' : ''}`} />
            </button>
          </div>
        </div>
        <div className="shrink-0 flex gap-2 px-4 py-3 border-t border-neutral-100">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-neutral-200 text-sm font-medium text-neutral-600 hover:bg-neutral-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-hover disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

// ─── Suppliers Panel ──────────────────────────────────────────────────────────

function SuppliersPanel({ restaurantId }) {
  const [suppliers,  setSuppliers]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [showAdd,    setShowAdd]    = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  useEffect(() => {
    setLoading(true);
    const unsub = onSnapshot(
      collection(db, 'restaurants_data', restaurantId, 'suppliers'),
      snap => { setSuppliers(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); },
      () => setLoading(false),
    );
    return unsub;
  }, [restaurantId]);

  const active   = suppliers.filter(s => s.isActive !== false);
  const inactive = suppliers.filter(s => s.isActive === false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-neutral-700">Supplier Catalog</h3>
          <p className="text-xs text-neutral-400">{active.length} active supplier{active.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="px-4 py-2 bg-brand-blue text-white text-sm font-semibold rounded-xl hover:bg-brand-blue-hover">
          + Add Supplier
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-brand-blue border-t-transparent rounded-full animate-spin" /></div>
      ) : suppliers.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-neutral-400">
          <p className="text-sm">No suppliers yet.</p>
          <button onClick={() => setShowAdd(true)} className="mt-3 text-brand-blue text-sm font-medium hover:underline">Add your first supplier</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {[...active, ...inactive].map(sup => (
            <div key={sup.id} className={`bg-white border rounded-xl p-4 flex flex-col gap-2 ${sup.isActive === false ? 'opacity-50 border-neutral-100' : 'border-neutral-100 shadow-sm'}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-neutral-800 leading-tight">{sup.name}</p>
                {sup.isActive === false && <span className="text-[10px] font-semibold uppercase px-2 py-0.5 bg-neutral-100 text-neutral-400 rounded-full shrink-0">Inactive</span>}
              </div>
              {sup.contactName && <p className="text-xs text-neutral-500">{sup.contactName}</p>}
              <div className="space-y-0.5">
                {sup.phone   && <p className="text-xs text-neutral-400">📞 {sup.phone}</p>}
                {sup.email   && <p className="text-xs text-neutral-400 truncate">✉ {sup.email}</p>}
                {sup.website && <p className="text-xs text-neutral-400 truncate">🌐 {sup.website}</p>}
              </div>
              {sup.notes && <p className="text-xs text-neutral-400 italic truncate">{sup.notes}</p>}
              <button
                onClick={() => setEditTarget(sup)}
                className="mt-auto pt-2 text-xs text-brand-blue font-medium hover:underline text-left"
              >
                Edit
              </button>
            </div>
          ))}
        </div>
      )}

      {(showAdd || editTarget) && (
        <SupplierModal
          supplier={editTarget || null}
          restaurantId={restaurantId}
          onClose={() => { setShowAdd(false); setEditTarget(null); }}
          onSaved={() => {}}
        />
      )}
    </div>
  );
}

// ─── Variance Panel ───────────────────────────────────────────────────────────

const VARIANCE_RANGES = [
  { value: '7',  label: 'Last 7 days'  },
  { value: '14', label: 'Last 14 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

function VariancePanel({ restaurantId }) {
  const [days,     setDays]     = useState('30');
  const [movements, setMovements] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const unsubRef = useRef(null);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);

    // Load all 'count' type movements — each one captures a physical count event
    // with previousQuantity (system expected) and newQuantity (actual counted)
    const q = query(
      collection(db, 'restaurants_data', restaurantId, 'inventoryMovements'),
      orderBy('timestamp', 'desc'),
      limit(2000),
    );
    if (unsubRef.current) unsubRef.current();
    unsubRef.current = onSnapshot(q, snap => {
      setMovements(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));

    return () => { if (unsubRef.current) unsubRef.current(); };
  }, [restaurantId]);

  // Filter to the selected date window and only count-type movements
  const cutoff = Date.now() - Number(days) * 24 * 60 * 60 * 1000;

  const countMovements = movements.filter(m => {
    if (m.type !== 'count') return false;
    const ts = m.timestamp?.toMillis ? m.timestamp.toMillis() : (m.timestamp || 0);
    return ts >= cutoff;
  });

  // Summarize depletion movements per ingredient in the same window
  const depletionByIng = {};
  movements
    .filter(m => {
      if (m.type !== 'depletion') return false;
      const ts = m.timestamp?.toMillis ? m.timestamp.toMillis() : (m.timestamp || 0);
      return ts >= cutoff;
    })
    .forEach(m => {
      depletionByIng[m.ingredientId] = (depletionByIng[m.ingredientId] || 0) + Math.abs(Number(m.quantity) || 0);
    });

  // Group count movements by ingredient — take most recent count per ingredient
  const byIng = {};
  for (const m of countMovements) {
    if (!byIng[m.ingredientId]) {
      byIng[m.ingredientId] = {
        ingredientId:   m.ingredientId,
        ingredientName: m.ingredientName,
        ingredientUnit: m.ingredientUnit,
        counts:         [],
      };
    }
    byIng[m.ingredientId].counts.push(m);
  }

  // Build variance rows — aggregate across all counts in window
  const rows = Object.values(byIng).map(entry => {
    const totalVariance = entry.counts.reduce((s, m) => s + (Number(m.quantity) || 0), 0);
    const latestCount   = entry.counts[0]; // already sorted desc
    const theoreticalUsage = depletionByIng[entry.ingredientId] || 0;
    const countCount    = entry.counts.length;

    // Variance % = variance relative to theoretical usage (or last system qty if no usage)
    const base = theoreticalUsage || Math.abs(Number(latestCount?.previousQuantity) || 0);
    const variancePct = base > 0 ? (totalVariance / base) * 100 : null;

    return {
      ...entry,
      totalVariance,
      variancePct,
      theoreticalUsage,
      countCount,
      lastCountedAt: latestCount?.timestamp,
    };
  }).sort((a, b) => (a.variancePct ?? 0) - (b.variancePct ?? 0)); // worst first

  // Summary stats
  const totalMissing   = rows.filter(r => r.totalVariance < 0).reduce((s, r) => s + Math.abs(r.totalVariance), 0);
  const highVariance   = rows.filter(r => r.variancePct != null && Math.abs(r.variancePct) > 15);
  const countedCount   = rows.length;

  // CSV export
  const handleExport = () => {
    const header = ['Ingredient', 'Unit', 'Variance (units)', 'Variance %', 'Theoretical Usage', 'Counts in Period', 'Last Counted'].join(',');
    const csvRows = rows.map(r => [
      `"${r.ingredientName}"`,
      r.ingredientUnit,
      r.totalVariance.toFixed(2),
      r.variancePct != null ? r.variancePct.toFixed(1) + '%' : 'N/A',
      r.theoreticalUsage.toFixed(2),
      r.countCount,
      r.lastCountedAt ? fmtDate(r.lastCountedAt) : '—',
    ].join(','));
    const csv = [header, ...csvRows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `variance-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Header + controls */}
      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-neutral-700">Variance Report</h3>
          <p className="text-xs text-neutral-400">Compare physical counts vs system-expected quantities</p>
        </div>
        <div className="flex gap-2">
          <select
            className="border border-neutral-200 rounded-xl px-3 py-2 text-sm text-neutral-600"
            value={days}
            onChange={e => setDays(e.target.value)}
          >
            {VARIANCE_RANGES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          {rows.length > 0 && (
            <button
              onClick={handleExport}
              className="px-4 py-2 border border-neutral-200 text-sm font-medium text-neutral-600 rounded-xl hover:bg-neutral-50 shrink-0"
            >
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* Summary strip */}
      {!loading && rows.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white border border-neutral-100 rounded-xl p-3 text-center shadow-sm">
            <p className="text-xl font-bold text-neutral-800">{countedCount}</p>
            <p className="text-xs text-neutral-400">Items Counted</p>
          </div>
          <div className={`border rounded-xl p-3 text-center shadow-sm ${highVariance.length > 0 ? 'bg-red-50 border-red-100' : 'bg-white border-neutral-100'}`}>
            <p className={`text-xl font-bold ${highVariance.length > 0 ? 'text-red-600' : 'text-neutral-800'}`}>{highVariance.length}</p>
            <p className="text-xs text-neutral-400">High Variance (&gt;15%)</p>
          </div>
          <div className={`border rounded-xl p-3 text-center shadow-sm ${totalMissing > 0 ? 'bg-amber-50 border-amber-100' : 'bg-white border-neutral-100'}`}>
            <p className={`text-xl font-bold ${totalMissing > 0 ? 'text-amber-700' : 'text-neutral-800'}`}>{fmtQty(totalMissing)}</p>
            <p className="text-xs text-neutral-400">Units Missing</p>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-brand-blue border-t-transparent rounded-full animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-neutral-400">
          <p className="text-sm">No count data in this period.</p>
          <p className="text-xs mt-1">Submit physical counts in the Count tab to see variance analysis.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Legend */}
          <div className="flex items-center gap-4 text-xs text-neutral-400 px-1 pb-1">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400" />Within 5%</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400" />5–15%</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400" />Over 15%</span>
          </div>

          {rows.map(row => {
            const pct = row.variancePct;
            const absPct = pct != null ? Math.abs(pct) : null;
            const isNeg  = row.totalVariance < 0;

            let dotColor = 'bg-neutral-300';
            let pctColor = 'text-neutral-500';
            let barColor = 'bg-neutral-200';
            if (absPct != null) {
              if (absPct <= 5) { dotColor = 'bg-green-400'; pctColor = 'text-green-600'; barColor = 'bg-green-400'; }
              else if (absPct <= 15) { dotColor = 'bg-yellow-400'; pctColor = 'text-yellow-600'; barColor = 'bg-yellow-400'; }
              else { dotColor = 'bg-red-400'; pctColor = 'text-red-600'; barColor = 'bg-red-400'; }
            }

            return (
              <div key={row.ingredientId} className="bg-white border border-neutral-100 rounded-xl px-4 py-3 space-y-2">
                <div className="flex items-start gap-2 justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-2 h-2 rounded-full shrink-0 mt-0.5 ${dotColor}`} />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-neutral-800 truncate">{row.ingredientName}</p>
                      <p className="text-xs text-neutral-400">
                        {row.countCount} count{row.countCount !== 1 ? 's' : ''} · last {fmtDate(row.lastCountedAt)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-bold ${isNeg ? 'text-red-600' : 'text-green-600'}`}>
                      {isNeg ? '' : '+'}{fmtQty(row.totalVariance)} {row.ingredientUnit}
                    </p>
                    {pct != null && (
                      <p className={`text-xs font-semibold ${pctColor}`}>
                        {isNeg ? '' : '+'}{pct.toFixed(1)}%
                      </p>
                    )}
                  </div>
                </div>

                {/* Mini bar showing variance magnitude */}
                {absPct != null && absPct > 0 && (
                  <div className="w-full h-1 bg-neutral-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${barColor}`}
                      style={{ width: `${Math.min(100, absPct * 2)}%` }}
                    />
                  </div>
                )}

                <div className="flex items-center gap-4 text-xs text-neutral-400">
                  {row.theoreticalUsage > 0 && (
                    <span>Theoretical usage: {fmtQty(row.theoreticalUsage)} {row.ingredientUnit}</span>
                  )}
                  {isNeg && (
                    <span className="text-red-400 font-medium">
                      {fmtQty(Math.abs(row.totalVariance))} unaccounted
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Prep List Panel ──────────────────────────────────────────────────────────

function todayLocal() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function PrepListPanel({ ingredients, restaurantId }) {
  const [reservations, setReservations] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const today = todayLocal();

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    const q = query(
      collection(db, 'restaurants_data', restaurantId, 'reservations'),
      where('dateOnly', '==', today),
    );
    const unsub = onSnapshot(q, snap => {
      setReservations(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [restaurantId, today]);

  // Exclude cancelled / no-show
  const CANCELLED = new Set(['cancelled', 'canceled', 'no_show', 'noshow']);
  const activeRes = reservations.filter(r => !CANCELLED.has((r.status || '').toLowerCase()));
  const totalCovers = activeRes.reduce((sum, r) => sum + (parseInt(r.partySize) || 0), 0);

  // Build prep list from ingredients with depletionPerCover set
  const prepItems = ingredients
    .filter(i => i.active && i.trackDepletion && Number(i.depletionPerCover) > 0)
    .map(i => {
      const needed   = Number(i.depletionPerCover) * totalCovers;
      const onHand   = Number(i.currentQuantity)   || 0;
      const shortfall = Math.max(0, needed - onHand);
      return { ...i, needed, onHand, shortfall };
    })
    .sort((a, b) => b.shortfall - a.shortfall);

  const uncoveredCount = prepItems.filter(p => p.shortfall > 0).length;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-neutral-700">Today's Prep List</h3>
          <p className="text-xs text-neutral-400">{today} · Based on today's reservations</p>
        </div>
        {!loading && (
          <div className="flex gap-2 shrink-0">
            <div className="bg-white border border-neutral-100 rounded-xl px-4 py-2 text-center shadow-sm">
              <p className="text-lg font-bold text-neutral-800 leading-none">{activeRes.length}</p>
              <p className="text-[10px] text-neutral-400 mt-0.5">Reservations</p>
            </div>
            <div className="bg-brand-blue-light border border-brand-blue/20 rounded-xl px-4 py-2 text-center shadow-sm">
              <p className="text-lg font-bold text-brand-blue leading-none">{totalCovers}</p>
              <p className="text-[10px] text-brand-blue/70 mt-0.5">Covers</p>
            </div>
            {uncoveredCount > 0 && (
              <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-2 text-center shadow-sm">
                <p className="text-lg font-bold text-red-600 leading-none">{uncoveredCount}</p>
                <p className="text-[10px] text-red-400 mt-0.5">Short</p>
              </div>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-6 h-6 border-2 border-brand-blue border-t-transparent rounded-full animate-spin" />
        </div>
      ) : prepItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-neutral-400">
          <p className="text-sm">No ingredients configured for cover-based forecasting.</p>
          <p className="text-xs mt-1 text-center max-w-xs">
            Enable "Track Depletion" on an ingredient and set "Depletion Per Cover" to generate a prep list.
          </p>
        </div>
      ) : (
        <>
          {totalCovers === 0 && (
            <div className="bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3 text-xs text-neutral-500">
              No reservations found for today — prep quantities shown as if covers = 0. Quantities will update automatically as reservations are added.
            </div>
          )}

          <div className="space-y-2">
            {prepItems.map(item => {
              const pct = item.onHand >= item.needed
                ? 100
                : item.needed > 0 ? Math.round((item.onHand / item.needed) * 100) : 0;
              const isShort = item.shortfall > 0;
              const barColor = isShort
                ? (pct < 50 ? 'bg-red-400' : 'bg-yellow-400')
                : 'bg-green-400';

              return (
                <div key={item.id} className={`bg-white border rounded-xl px-4 py-3 space-y-2 ${isShort ? 'border-red-200' : 'border-neutral-100'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-neutral-800 truncate">{item.name}</p>
                      <p className="text-xs text-neutral-400">
                        {fmtQty(item.depletionPerCover)} {item.baseUnit} × {totalCovers} covers
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-neutral-800">
                        {fmtQty(item.needed)} <span className="text-xs font-normal text-neutral-400">{item.baseUnit}</span>
                      </p>
                      <p className="text-xs text-neutral-400">needed</p>
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <span className="text-neutral-500">
                      On hand: <span className="font-semibold text-neutral-700">{fmtQty(item.onHand)} {item.baseUnit}</span>
                    </span>
                    {isShort ? (
                      <span className="font-semibold text-red-600">
                        Short {fmtQty(item.shortfall)} {item.baseUnit}
                      </span>
                    ) : (
                      <span className="text-green-600 font-semibold">Sufficient</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ─── InventoryView (root) ─────────────────────────────────────────────────────

const SUB_TABS = [
  { id: 'ingredients', label: 'Ingredients' },
  { id: 'count',       label: 'Count'       },
  { id: 'movements',   label: 'Movements'   },
  { id: 'orders',      label: 'Orders'      },
  { id: 'suppliers',   label: 'Suppliers'   },
  { id: 'cogs',        label: 'Recipe Cost' },
  { id: 'variance',    label: 'Variance'    },
  { id: 'prep',        label: 'Prep List'   },
];

export default function InventoryView({ user, restaurantId }) {
  const [activeTab,    setActiveTab]    = useState('ingredients');
  const [ingredients,  setIngredients]  = useState([]);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    if (!restaurantId) return;
    setLoading(true);
    const q = query(
      collection(db, 'restaurants_data', restaurantId, 'ingredients'),
      orderBy('name', 'asc'),
    );
    const unsub = onSnapshot(q, snap => {
      setIngredients(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));

    return unsub;
  }, [restaurantId]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Sub-tab bar */}
      <div className="shrink-0 flex border-b border-neutral-200 bg-white px-4">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === t.id
                ? 'border-brand-blue text-brand-blue'
                : 'border-transparent text-neutral-400 hover:text-neutral-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Panel body */}
      <div className="flex-1 overflow-y-auto p-4">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-7 h-7 border-2 border-brand-blue border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {activeTab === 'ingredients' && (
              <IngredientsPanel
                ingredients={ingredients}
                restaurantId={restaurantId}
                user={user}
              />
            )}
            {activeTab === 'count' && (
              <CountPanel
                ingredients={ingredients}
                restaurantId={restaurantId}
                user={user}
              />
            )}
            {activeTab === 'movements' && (
              <MovementsPanel
                ingredients={ingredients}
                restaurantId={restaurantId}
                user={user}
              />
            )}
            {activeTab === 'orders' && (
              <PurchaseOrdersPanel
                ingredients={ingredients}
                restaurantId={restaurantId}
                user={user}
              />
            )}
            {activeTab === 'suppliers' && (
              <SuppliersPanel restaurantId={restaurantId} />
            )}
            {activeTab === 'cogs' && (
              <COGSPanel ingredients={ingredients} restaurantId={restaurantId} />
            )}
            {activeTab === 'variance' && (
              <VariancePanel restaurantId={restaurantId} />
            )}
            {activeTab === 'prep' && (
              <PrepListPanel ingredients={ingredients} restaurantId={restaurantId} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
